import type { EnrollmentRequest, PatientEvent, RosterRequest, Session } from '@oncocoach/protocol';
import { SESSION_KEY } from './ssoLogout';
import type {
  AppSession,
  AuthResult,
  CoachAskResponse,
  CoachTurn,
  DevicesView,
  EmailSignInStartResult,
  ImportResult,
  InboxView,
  InviteOutcome,
  InviteView,
  MeView,
  Message,
  OutreachSettings,
  OutreachView,
  PatientDetail,
  PatientView,
  PracticeView,
  Role,
  ThreadSummary,
  ThreadView,
} from './types';
import type { AuthConfig } from './home';

/** Base URL of the API. `/api` is proxied by Vite in dev; baked at build otherwise. */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/+$/, '');

const VIAS: ReadonlyArray<AppSession['via']> = ['home', 'dev', 'demo', 'email'];

export function loadSession(): AppSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<AppSession>;
    if (typeof s.token === 'string' && typeof s.personId === 'string' && typeof s.name === 'string') {
      const via = s.via && VIAS.includes(s.via) ? s.via : s.personId.startsWith('home:') ? 'home' : s.personId.startsWith('email:') ? 'email' : 'dev';
      return {
        token: s.token,
        personId: s.personId,
        name: s.name,
        via,
        address: typeof s.address === 'string' ? s.address : undefined,
        agentName: typeof s.agentName === 'string' ? s.agentName : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveSession(session: AppSession | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage blocked: memory only */
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

async function request<T>(path: string, init: RequestInit = {}, token?: string, notifyUnauthorized = true): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers, ...(init.headers as Record<string, string>) } });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    if (res.status === 401 && token && notifyUnauthorized) unauthorizedHandler?.();
    const msg = body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string' ? (body as { error: string }).error : typeof body === 'string' && body ? body : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

export interface HomeSessionResponse {
  token: string;
  personId: string;
  name: string;
  agentName?: string;
  address?: string;
}

export interface HomeAuthBody {
  code: string;
  codeVerifier: string;
  authOrigin: string;
  nonce: string;
  state: string;
  profileName?: string;
}

export interface DemoAuthBody {
  idToken: string;
  delegation?: unknown;
  authOrigin: string;
}

/** A patient to add to the list, as the manager typed or pasted them. */
export interface NewPatient {
  name: string;
  email: string;
  doctorId?: string;
  seedDemo?: boolean;
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const enc = encodeURIComponent;

/** The browser's offset in minutes east of UTC, which is what the care package calls a time zone. */
export function localTzOffset(): number {
  return -new Date().getTimezoneOffset();
}

export const api = {
  authConfig: () => request<AuthConfig>('/auth/config'),
  homeLogin: (body: HomeAuthBody) => request<HomeSessionResponse>('/auth/home', json(body)),
  demoLogin: (body: DemoAuthBody) => request<HomeSessionResponse>('/auth/home/demo', json(body)),
  signOut: (token: string) => request<{ ok: boolean }>('/auth/signout', json({}), token, false).catch(() => ({ ok: false })),
  homeMandate: (body: HomeAuthBody, token: string) => request<unknown>('/auth/home/mandate', json(body), token),
  devLogin: (name: string, role: Role) => request<Session & { personId: string; role: Role }>('/dev/session', json({ name, role })),
  emailStart: (email: string) => request<EmailSignInStartResult>('/auth/email/start', json({ email })),
  emailComplete: (token: string) => request<AuthResult>('/auth/email/complete', json({ token })),
  inviteInfo: (inviteToken: string) => request<InviteView>(`/invites/${enc(inviteToken)}`),
  acceptInvite: (inviteToken: string, email: string) => request<AuthResult>(`/invites/${enc(inviteToken)}/accept`, json({ email, tzOffsetMinutes: localTzOffset() })),

  me: (token: string) => request<MeView>('/me', {}, token),
  demoJoin: (token: string) => request<{ ok: true }>('/demo/join', json({}), token),

  patient: (token: string) => request<PatientView>('/patient', {}, token),
  postEvent: (event: PatientEvent, token: string) => request<{ view: PatientView; insight: string | null }>('/patient/events', json(event), token),
  inbox: (token: string) => request<InboxView>('/patient/inbox', {}, token),
  readMessage: (id: string, token: string) => request<{ unread: number }>(`/patient/inbox/${enc(id)}/read`, { method: 'POST' }, token),
  patientThread: (token: string) => request<ThreadView>('/patient/thread', {}, token),
  outreach: (settings: OutreachSettings, token: string) => request<OutreachView>('/patient/outreach', json(settings), token),
  devices: (token: string) => request<DevicesView>('/patient/devices', {}, token),
  linkDevice: (token: string) => request<{ url: string; provider: string; externalUserId: string }>('/patient/devices/link', json({}), token),
  unlinkDevice: (id: string, token: string) => request<{ ok: true; devices: DevicesView['devices'] }>(`/patient/devices/${enc(id)}/unlink`, json({}), token),
  messageCareTeam: (text: string, token: string) => request<Message>('/patient/messages', json({ text }), token),
  coachAsk: (question: string, history: CoachTurn[], token: string) => request<CoachAskResponse>('/coach/ask', json({ question, history }), token),

  practice: (token: string) => request<PracticeView>('/practice', {}, token),
  practicePatient: (id: string, token: string) => request<PatientDetail>(`/practice/patients/${enc(id)}`, {}, token),
  sendMessage: (id: string, text: string, token: string) => request<Message>(`/practice/patients/${enc(id)}/message`, json({ text }), token),
  sendReminder: (id: string, text: string, scheduledAt: number | undefined, token: string) => request<Message>(`/practice/patients/${enc(id)}/reminder`, json(scheduledAt ? { text, scheduledAt } : { text }), token),
  ackAlert: (id: string, token: string) => request<unknown>(`/practice/alerts/${enc(id)}/ack`, { method: 'POST' }, token),
  enroll: (body: Partial<EnrollmentRequest> & { patient: string; name: string }, token: string) => request<{ ok: true; patientId: string; doctor: string }>('/practice/enroll', json(body), token),
  addRoster: (body: RosterRequest, token: string) => request<{ roster: PracticeView['roster'] }>('/practice/roster', json(body), token),
  removeRoster: (personId: string, token: string) => request<{ ok: true }>(`/practice/roster/${enc(personId)}`, { method: 'DELETE' }, token),
  createPractice: (body: { org: string; name: string; orgName?: string }, token: string) => request<{ ok: true; practiceId: string; name: string }>('/practice/create', json(body), token),

  importPatients: (patients: NewPatient[], invite: boolean, token: string) => request<ImportResult>('/practice/patient-list', json({ patients, invite }), token),
  invitePatient: (entryId: string, token: string) => request<InviteOutcome>(`/practice/patient-list/${enc(entryId)}/invite`, json({}), token),
  invitePending: (token: string) => request<{ invited: InviteOutcome[]; failed: Array<{ name: string; reason: string }> }>('/practice/patient-list/invite-pending', json({}), token),
  removeListEntry: (entryId: string, token: string) => request<{ ok: true }>(`/practice/patient-list/${enc(entryId)}`, { method: 'DELETE' }, token),
  threads: (token: string) => request<{ threads: ThreadSummary[]; unread: number }>('/practice/threads', {}, token),
  thread: (patientId: string, token: string) => request<ThreadView>(`/practice/threads/${enc(patientId)}`, {}, token),

};
